import { useState, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import FAQAccordion from '../components/knowledge/FAQAccordion';
import CreateTicketModal from '../components/knowledge/CreateTicketModal';
import FloatingHelpButton from '../components/knowledge/FloatingHelpButton';
import { MessageSquare, HelpCircle } from 'lucide-react';

const FAQ_DATA = [
  {
    id: 1,
    category: 'Getting Started',
    question: 'How do I create a new ticket?',
    answer:
      'Go to the "Create Ticket" page from the sidebar, fill in the required details including subject, description, category, and priority.',
  },
  {
    id: 2,
    category: 'Getting Started',
    question: 'Who can see my tickets?',
    answer:
      'Only you, assigned support staff, and administrators can view your tickets.',
  },
  {
    id: 3,
    category: 'Technical Support',
    question: 'How do I attach files to a ticket?',
    answer:
      'Use the Attachments section when creating or updating a ticket.',
  },
  {
    id: 4,
    category: 'Account & Access',
    question: 'I forgot my password. What should I do?',
    answer:
      'Use the "Forgot Password" option on the login page.',
  },
];

const KnowledgeFaqs = () => {
  const { user } = useAuth();
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketInitial, setTicketInitial] = useState({ subject: '', description: '' });
  const [selectedCategory, setSelectedCategory] = useState('all');

  /* ---------------- Logic (unchanged) ---------------- */

  const faqsByCategory = useMemo(() => {
    const grouped = FAQ_DATA.reduce((acc, faq) => {
      if (!acc[faq.category]) acc[faq.category] = [];
      acc[faq.category].push(faq);
      return acc;
    }, {});
    return selectedCategory === 'all'
      ? grouped
      : { [selectedCategory]: grouped[selectedCategory] || [] };
  }, [selectedCategory]);

  const categories = useMemo(
    () => ['all', ...new Set(FAQ_DATA.map(faq => faq.category))],
    []
  );

  const handleCreateTicketFromFaq = ({ subject, description }) => {
    setTicketInitial({ subject, description });
    setTicketModalOpen(true);
  };

  /* ================= UI ================= */

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 pb-20">
        
        {/* === COMPACT STICKY HEADER (Yellow Theme) === */}
        <div className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-yellow-200/50 dark:border-yellow-900/30 transition-all">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              
              {/* Left: Icon & Title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-yellow-500/20 shrink-0">
                  <HelpCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    Frequently Asked Questions
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                    Common questions and support topics
                  </p>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    handleCreateTicketFromFaq({
                      subject: 'Support Request from FAQs',
                      description: 'User reviewed FAQs but needs additional assistance.',
                    })
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-yellow-500/20"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Get Help
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs - Below Header */}
        <div className="sticky top-[73px] z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-800">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                  selectedCategory === 'all'
                    ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800'
                    : 'bg-transparent text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                All Categories
              </button>
              <div className="w-px h-4 bg-gray-300 dark:bg-slate-700 mx-1 flex-shrink-0" />
              {categories.filter(c => c !== 'all').map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                    selectedCategory === category
                      ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800'
                      : 'bg-transparent text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ================= MAIN CONTENT ================= */}
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 mt-6 space-y-8">

          {/* FAQ Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-lg p-6 md:p-10">
            <FAQAccordion
              faqsByCategory={faqsByCategory}
              onCreateTicket={handleCreateTicketFromFaq}
            />
          </div>

          {/* ================= HELP CTA ================= */}
          <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-800 dark:via-indigo-950 dark:to-purple-950 rounded-3xl border border-indigo-200/60 dark:border-indigo-800/60 shadow-lg shadow-indigo-500/10 dark:shadow-black/20 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-700 flex items-center justify-center shadow-md shadow-indigo-500/20">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
                    Still need help?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    If you couldn't find what you were looking for, our support team is ready.
                  </p>
                </div>
              </div>

              <button
                onClick={() =>
                  handleCreateTicketFromFaq({
                    subject: 'Support Request from FAQs',
                    description: 'User reviewed FAQs but needs additional assistance.',
                  })
                }
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 transition-all whitespace-nowrap"
              >
                Create Support Ticket
              </button>
            </div>
          </div>
        </div>

        <CreateTicketModal
          isOpen={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          initialSubject={ticketInitial.subject}
          initialDescription={ticketInitial.description}
        />

        <FloatingHelpButton />
      </div>
    </DashboardLayout>
  );
};

export default KnowledgeFaqs;
